// app/hooks/useContactForm.ts
'use client';
import { post } from 'aws-amplify/api';
import { useState, type ChangeEvent } from 'react';
import { isValidEmail } from '@/utils/check-email';
import { toast } from 'react-toastify';
import config from '../config';
import { useRecaptcha } from './useRecaptcha';
import { sendGAEvent } from '@next/third-parties/google';

export interface FormData {
  name: string;
  email: string;
  message: string;
}

export interface FormError {
  email: boolean;
  required: boolean;
  recaptcha: boolean;
}

export const useContactForm = (): {
  input: FormData;
  error: FormError;
  isLoading: boolean;
  handleInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleBlur: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: () => Promise<void>;
} => {
  const [input, setInput] = useState<FormData>({
    name: '',
    email: '',
    message: '',
  });
  const [error, setError] = useState<FormError>({
    email: false,
    required: false,
    recaptcha: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { executeRecaptcha } = useRecaptcha();

  const checkRequired = () => {
    if (input.email && input.message && input.name) {
      setError((prev) => ({ ...prev, required: false }));
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setInput((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleBlur = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    checkRequired();
    if (e.target.name === 'email') {
      setError((prev) => ({ ...prev, email: !isValidEmail(input.email) }));
    }
  };

  const handleSubmit = async () => {
    if (!input.email || !input.message || !input.name) {
      setError((prev) => ({ ...prev, required: true }));
      return;
    }
    if (error.email) {
      return;
    }

    setError((prev) => ({ ...prev, required: false, recaptcha: false }));
    setIsLoading(true);

    try {
      // Execute reCAPTCHA
      const recaptchaToken = await executeRecaptcha('contact_form');
      if (!recaptchaToken) {
        setError((prev) => ({ ...prev, recaptcha: true }));
        toast.error('reCAPTCHA verification failed. Please try again.');
        return;
      }

      const bodyPayload = {
        name: input.name,
        email: input.email,
        message: input.message,
        recaptchaToken,
      };
      const restOperation = post({
        apiName: 'portfolio-website',
        path: '/email/send',
        options: {
          body: bodyPayload,
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': config.apiGateway.EMAIL_API_KEY,
          },
        },
      });
      const { body, statusCode } = await restOperation.response;
      const response = await body.json();

      if (statusCode === 200) {
        toast.success('Message sent successfully!');
        sendGAEvent('event', 'form_submit', { form_name: 'contact_form' });
        setInput({ name: '', email: '', message: '' });
      } else {
        console.error('Error sending message:', { errorResponse: response });
        const errorResponse = response as { error?: string };
        toast.error(errorResponse?.error || 'Error sending message');
      }
    } catch (err: unknown) {
      console.error('Error sending message:', {
        err,
      });
      toast.error('An unexpected error occurred!');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    input,
    error,
    isLoading,
    handleInputChange,
    handleBlur,
    handleSubmit,
  };
};
